/**
 * Sync Store — Phase 6 (RxDB)
 *
 * Provides reactive sync status by aggregating RxDB per-collection replication
 * states.
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { combineLatest, merge, type Subscription } from "rxjs";
import { startWith, map } from "rxjs/operators";
import {
  syncConfigurationService,
} from "@/services/syncConfiguration";
import {
  getReplicationStates,
  reSyncAll,
  reSyncAllAndWait,
  ensureReplication,
  flushDiaryReplication,
  stopReplication,
} from "@/services/rxdb/replication";
import {
  startRealtimeSync,
  stopRealtimeSync,
} from "@/services/realtimeSync";
import type { SyncConfiguration } from "@/types/sync";

// -------------------------------------------------------------------------
// Re-export types so existing import sites stay happy
// -------------------------------------------------------------------------
export type SyncStatus = {
  isOnline: boolean;
  lastSyncAt?: string;
  pendingSyncCount: number;
  isSyncing: boolean;
  lastError?: string;
};
export type { SyncConfiguration };

const defaultStatus = (): SyncStatus => ({
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  pendingSyncCount: 0,
  isSyncing: false,
});

const getCurrentUserId = async (): Promise<string | null> => {
  const { useAuthSessionStore } = await import("@/stores/authSessionStore");
  return useAuthSessionStore().user?.id ?? null;
};

export const useSyncStore = defineStore("sync", () => {
  const status = ref<SyncStatus>(defaultStatus());
  const configuration = ref<SyncConfiguration | null>(null);

  // Derived convenience flags (same API as legacy store)
  const isOnline = computed(() => status.value.isOnline);
  const isSyncing = computed(() => status.value.isSyncing);
  const lastSyncAt = computed(() => status.value.lastSyncAt);
  const lastError = computed(() => status.value.lastError);
  const pendingSyncCount = computed(() => status.value.pendingSyncCount);

  // -----------------------------------------------------------------------
  // Internal subscriptions
  // -----------------------------------------------------------------------
  let activeSub: Subscription | null = null;
  let errorSub: Subscription | null = null;
  let onlineSub: (() => void) | null = null;

  function startListening(): void {
    if (activeSub) return; // already listening

    void (async () => {
      const userId = await getCurrentUserId();
      if (!userId) return;
      const config = await loadConfiguration();
      if (!config.enabled) return;

      // Ensure replication is started before subscribing to its states.
      await ensureReplication(userId);
      startRealtimeSync(userId);
      const states = getReplicationStates(userId);
      if (states.length === 0) return;

      // Aggregate active$ — true when any collection is running a cycle.
      activeSub = combineLatest(
        states.map((s) => s.active$.pipe(startWith(false))),
      )
        .pipe(map((actives) => actives.some(Boolean)))
        .subscribe((nowSyncing) => {
          const wasSyncing = status.value.isSyncing;
          if (wasSyncing && !nowSyncing) {
            // Transition from syncing → idle: record last sync time
            status.value = {
              ...status.value,
              isSyncing: false,
              lastSyncAt: new Date().toISOString(),
              lastError: undefined,
            };
          } else {
            status.value = { ...status.value, isSyncing: nowSyncing };
          }
        });

      // Aggregate error$ — surface the most recent replication error.
      errorSub = merge(...states.map((s) => s.error$)).subscribe((err) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === "object" && err !== null && "message" in err
              ? String((err as { message: unknown }).message)
              : "Sync error";
        status.value = { ...status.value, lastError: message };
      });
    })();

    // Online / offline events
    const handleOnline = () => {
      status.value = { ...status.value, isOnline: true };
      void getCurrentUserId().then((userId) => {
        if (userId) reSyncAll(userId);
      });
    };
    const handleOffline = () => (status.value = { ...status.value, isOnline: false });
    const handlePageHide = () => {
      void getCurrentUserId().then((userId) => {
        if (userId) flushDiaryReplication(userId);
      });
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("pagehide", handlePageHide);
    onlineSub = () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }

  function stopListening(): void {
    activeSub?.unsubscribe();
    errorSub?.unsubscribe();
    activeSub = null;
    errorSub = null;
    onlineSub?.();
    onlineSub = null;
    stopRealtimeSync();
  }

  // -----------------------------------------------------------------------
  // Configuration
  // -----------------------------------------------------------------------

  async function loadConfiguration(): Promise<SyncConfiguration> {
    const config = await syncConfigurationService.loadConfiguration();
    configuration.value = config;
    return config;
  }

  async function setSyncEnabled(enabled: boolean): Promise<void> {
    await syncConfigurationService.setSyncEnabled(enabled);
    if (configuration.value) {
      configuration.value = { ...configuration.value, enabled };
    }
    stopListening();
    const userId = await getCurrentUserId();
    if (!userId) return;
    if (enabled) {
      startListening();
    } else {
      await stopReplication(userId);
      status.value = { ...status.value, isSyncing: false };
    }
  }

  // -----------------------------------------------------------------------
  // Sync triggers
  // -----------------------------------------------------------------------

  async function sync(): Promise<void> {
    const config = await loadConfiguration();
    if (!config.enabled) return;
    const userId = await getCurrentUserId();
    if (!userId) return;
    await ensureReplication(userId);
    await reSyncAllAndWait(userId);
  }

  async function forceSync(): Promise<void> {
    await sync();
  }

  return {
    status,
    configuration,
    isOnline,
    isSyncing,
    lastSyncAt,
    lastError,
    pendingSyncCount,
    startListening,
    stopListening,
    loadConfiguration,
    setSyncEnabled,
    sync,
    forceSync,
  };
});
